// The owner/GC sign-off on paper (owner, 2026-10-21: "add the blue star to
// the printed schedule and export"). The feed carries row.ownerSignOff from
// the server (utils/ownerSignOff.decorateActivities): `star` on the topmost
// rows a toggle covers, `inherited` on the rows under them. Paper cannot
// hover, so what the schedule's tooltip says goes into the cell.
import { OWNER_STAR_BLUE, OWNER_STAR_PLANNED } from "components/Coordination/OwnerSignOffToggle";
import { OWNER_SIGNOFF_CHOICES } from "components/Coordination/OwnerSignOffFilter";
import { ownerStateOfItem } from "components/CoordinationGantt/ganttDayFilters";

export { OWNER_STAR_BLUE, OWNER_STAR_PLANNED };

/** The day the blue star stands on, as a local-midnight Date (null when none). */
export function ownerStarDay(it) {
  const o = it && it.ownerSignOff;
  if (!o || !o.date) return null;
  const d = new Date(`${o.date}T00:00:00`);
  return Number.isNaN(+d) ? null : d;
}

/** The star drawn on this row's timeline: only the topmost covered rows get one. */
export function ownerStarOn(it) {
  return !!(it && it.ownerSignOff && it.ownerSignOff.star && ownerStarDay(it));
}

export const ownerStarColor = (it) => (it && it.ownerSignOff && it.ownerSignOff.planned ? OWNER_STAR_PLANNED : OWNER_STAR_BLUE);

/**
 * The words for a cell: "★ Oct 21, 26" / "☆ planned Oct 21, 26", and
 * "↳ Oct 21, 26" on a row covered by the one above it. `long` adds the scope
 * and who marked it, for the report's detail lines.
 */
export function ownerSignOffText(it, fmt, { long = false } = {}) {
  const o = it && it.ownerSignOff;
  const day = ownerStarDay(it);
  if (!o || !day) return "";
  const mark = o.inherited ? "↳" : o.planned ? "☆" : "★";
  const head = `${mark} ${o.planned ? "planned " : ""}${fmt(day)}`;
  if (!long) return head;
  const scope = o.scope ? `${o.scope}${o.inherited ? " (above this row)" : ""}` : "";
  return [head, scope, o.by ? `by ${o.by}` : ""].filter(Boolean).join(" · ");
}

// THE OWNER/GC FILTER ON PAPER (owner, 2026-10-22: "add the owner/GC filter
// to the schedule print too"). The schedule chart's rule, so the sheet prints
// what the chart showed: a row in a picked state is kept with everything
// under it, and its parents come along for context only.
//
// ownerFilterScope(items, "signed,not") -> null when no filter is on, else
// { keep: Set of ids to draw, context: Set of the parents kept for context }.
export function ownerFilterScope(items, key) {
  const states = String(key || "").split(",").filter(Boolean);
  if (!states.length) return null;
  const byId = new Map(items.map((i) => [String(i._id), i]));
  const kids = new Map();
  for (const it of items) {
    if (!it.parentId) continue;
    const p = String(it.parentId);
    if (!kids.has(p)) kids.set(p, []);
    kids.get(p).push(String(it._id));
  }
  const hit = new Set();
  for (const it of items) {
    if (!states.includes(ownerStateOfItem(it))) continue;
    const stack = [String(it._id)];
    while (stack.length) { const c = stack.pop(); if (hit.has(c)) continue; hit.add(c); stack.push(...(kids.get(c) || [])); }
  }
  const context = new Set();
  for (const id of hit) {
    let p = byId.get(id) && byId.get(id).parentId ? byId.get(String(byId.get(id).parentId)) : null;
    while (p && !hit.has(String(p._id)) && !context.has(String(p._id))) { context.add(String(p._id)); p = p.parentId ? byId.get(String(p.parentId)) : null; }
  }
  return { keep: new Set([...hit, ...context]), context };
}

/** "Signed off, Planned" - the words for the sheet's header line. */
export const ownerFilterWords = (key) => String(key || "").split(",").filter(Boolean)
  .map((v) => (OWNER_SIGNOFF_CHOICES.find((c) => c.value === v) || {}).text || v).join(", ");

// The links between the two print pages carry the scope and the filter.
export const printQuery = (company, owner) => {
  const q = new URLSearchParams();
  if (company) q.set("company", company);
  if (owner) q.set("ownerSignOff", owner);
  const str = q.toString();
  return str ? `?${str}` : "";
};
